import { Edit, ExternalLink, Trash } from 'react-feather';
import { Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { gql } from '__generated__';

import {
  GetAppsForProjectQuery as GetAppsForProject,
  Permission,
} from '__generated__/graphql';

import { useNotifier } from '../../common/Notifications/NotificationProvider';
import { useSession } from '../Auth/SessionProvider';
import GetAppsForProjectQuery from './GetAppsForProjectQuery';
import OptionsDropdown, { Option } from '../../common/Common/OptionsDropdown';
import UI from '../../common/UI';
import useAddAppModal from './useAddAppModal';
import useConfirmModal from '../../common/useConfirmModal';
import useEditAppPermissionsModal from './useEditAppPermissionsModal';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';
import useMutation from '../../api/useMutation';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

type Project = GetAppsForProject['project'];
type App = Project['app_authorizations'][0]['app'];

export const RemoveAppMutation = gql(`
  mutation RemoveAppFromProject($project_id: ID!, $app_id: ID!) {
    removeAppFromProject(
      project: { id: $project_id },
      app: { id: $app_id }
    ) {
      id
      app_authorizations {
        permissions
        app {
          id
          name
        }
      }
    }
  }
`);

interface AppOptionsDropdownProps {
  app: App;
  permissions: Permission[];
}

const AppOptionsDropdown = ({ app, permissions }: AppOptionsDropdownProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const [removeApp] = useMutation(
    RemoveAppMutation,
    {
      variables: {
        project_id: project.id,
        app_id: app.id,
      },
      onCompleted: () => {
        notifier.success(t('notifications.app_removed'));
      },
    },
  );

  const [confirmRemove] = useConfirmModal({
    title: t('remove_app'),
    body: (
      <Trans
        i18nKey="confirm_remove_app"
        values={{
          name: app.name,
          project: project.organisation_name,
        }}
      >
        <UI.Strong />
      </Trans>
    ),
    onConfirm: () => removeApp(),
  }, []);

  const [openEditAppPermissionsModal] = useEditAppPermissionsModal({
    app,
    permissions,
  });

  return (
    <OptionsDropdown name={app.name}>
      <Option
        icon={<Edit />}
        label={t('edit')}
        aria-label={t('edit_app')}
        onClick={() => openEditAppPermissionsModal()}
      />
      <Option
        icon={<Trash />}
        label={t('remove')}
        aria-label={t('remove_app')}
        onClick={() => confirmRemove()}
      />
    </OptionsDropdown>
  );
};

const AppsList = ({ project }: GetAppsForProject) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const { appPermissions: availablePermissions } = useSession();

  const device = useMediaDevice();

  return (
    <UI.GridContainer
      sc={{
        columns: device.isSmall ? '30px 2fr 145px 34px' : '30px 1fr 1fr 145px 34px',
        gutter: 0.5,
        alignVertical: 'center',
      }}
    >
      {project.app_authorizations.map(({ app, permissions }) => (
        <Fragment key={app.id}>
          <UI.Div>
            <UI.ProfilePic
              sc={{
                background: 'secondary',
                fit: 'cover',
                size: 30,
              }}
            >
              <UI.Span><UI.Span>{app.name.charAt(0).toUpperCase()}</UI.Span></UI.Span>
            </UI.ProfilePic>
          </UI.Div>
          <UI.Div>
            <UI.Truncate>
              {app.name}
            </UI.Truncate>
          </UI.Div>
          {!device.isSmall && (
            <UI.Div sc={{ color: 'gray.500' }}>
              {formatDate(parseDate(app.created_at), { format: 'display_compact_date' })}
            </UI.Div>
          )}
          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.Label sc={{ color: 'gray.600', small: true }} style={{ fontWeight: 500 }}>
              {permissions.length === availablePermissions.length
                ? t('full_access')
                : t('restricted_access')}
            </UI.Label>
          </UI.Div>
          <UI.Div>
            <AppOptionsDropdown app={app} permissions={permissions} />
          </UI.Div>
        </Fragment>
      ))}
    </UI.GridContainer>
  );
};

interface ApiPageProps {
  projectId: string;
}

const ApiPage = ({ projectId }: ApiPageProps) => {
  const { data } = useQuery(GetAppsForProjectQuery, {
    variables: {
      project_id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return <></>;
  }

  return <AppsView project={project} />;
};

interface AppsViewProps {
  project: Project;
}

const AppsView = ({ project }: AppsViewProps) => {
  const { t } = useTranslation();
  const hasAuthorizedApps = project.app_authorizations.length > 0;

  const [openAddAppModal] = useAddAppModal();

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <UI.H3>
          {t('atleta_api')}
        </UI.H3>

        <UI.FlexContainer sc={{ spaceX: 1 }}>
          <UI.AButton
            href="https://atleta.cc/article/api"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('documentation')}
            {' '}
            <UI.Icon>
              <ExternalLink />
            </UI.Icon>
          </UI.AButton>
          <UI.Button
            onClick={openAddAppModal}
            sc={{ brand: 'secondary' }}
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('add_app')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.InputDescription sc={{ my: 3 }}>
        {t('app_access_description')}
      </UI.InputDescription>

      <UI.Card
        sc={{
          hasPanels: true,
          textAlign: hasAuthorizedApps ? undefined : 'center',
          background: hasAuthorizedApps ? undefined : 'gray.50',
        }}
      >
        <UI.Div>
          {hasAuthorizedApps ? <AppsList project={project} /> : t('no_apps_yet')}
        </UI.Div>
      </UI.Card>
    </UI.Container>
  );
};

export default ApiPage;
